import { useState } from "react";

import { Alert, Span } from "@icg360/design-system";

import { SpinnerLoader } from "components/loader/loader";
import { MultiStepFormLayout } from "components/multi-step-form-layout";
import {
  ABOVE_GROUND_POOL_STEP_COUNT,
  IN_GROUND_POOL_STEP_COUNT,
  NO_POOL_STEP_COUNT,
  POOL_DISPLAY_ENUM_MAP,
} from "consts";
import {
  useKeystone,
  usePolicyDetail,
  usePolicyId,
  useUser,
} from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { usePolling } from "hooks/use-polling";
import { logError, trackEvent, useFlags } from "utils";
import { type PropertySection } from "utils/keystone-api-mapping";

import {
  PropertyUpdatePoolContext,
  type PropertyUpdatePoolContextState,
} from ".";
import { FileThumbnails } from "../file-thumbnails";
import { usePropertyUpdatePool } from "./hooks";
import styles from "./property-update-pool.module.scss";

const POLLING_TIMEOUT = 30000; // Thirty seconds
const POLLING_INTERVAL = 1000; // One second

export const PoolReview = () => {
  const keystone = useKeystone();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>();
  const { supportPhoneNumber } = useFlags();
  const user = useUser();
  const userEmail = user?.email ?? "";
  const insightPolicyId = usePolicyId();
  const { mutate: refreshPolicyData } = usePolicyDetail();
  const { prevPoolType } = usePropertyUpdatePool();
  const { formNavigate, state } =
    useMultiStepFormState<PropertyUpdatePoolContextState>(
      PropertyUpdatePoolContext
    );
  const { files, poolType } = state ?? {};

  const { startPolling } = usePolling({
    async action() {
      return await refreshPolicyData();
    },
    predicate(data) {
      const poolUpdateData =
        data?.userPropertyProfileData?.propertyUpdateData?.find(
          (detail) => detail?.detailName === "POOL"
        );
      const hasPoolTypeUpdate =
        poolUpdateData?.items?.find((item) => item?.termName === "Pool Type")
          ?.enumerationValue === POOL_DISPLAY_ENUM_MAP[poolType];

      return hasPoolTypeUpdate;
    },
    onComplete() {
      formNavigate("../success");
    },
    timeout: POLLING_TIMEOUT,
    interval: POLLING_INTERVAL,
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    const request = {
      insightPolicyId,
      userEmail,
      propertySection: "POOL" as PropertySection,
      propertyUpdateItems: [
        {
          termName: "Pool Type",
          enumerationValue: POOL_DISPLAY_ENUM_MAP[poolType],
          currentDisplayValue: prevPoolType ?? "N/A",
          updatedDisplayValue: poolType,
        },
        // TODO: Rest of values...
      ],
    };

    try {
      const response = await keystone.updateProperty(request, files);

      if (!isError(response)) {
        startPolling();
      } else {
        setSubmitting(false);
        setError(response.message);
        logError(`Pool update submission error: ${response.message}`);
        trackError({
          errorMessage: response.message,
          errorType: "server_error",
        });
      }
    } catch (err) {
      setSubmitting(false);
      setError(err);
      logError(`Pool update submission error: (${err?.name}: ${err?.message})`);
      trackError({
        errorCode: err?.code,
        errorMessage: err?.message,
        errorType: "system_error",
      });
    }
  };

  type PropertyErrorProps = {
    errorCode?: string | number;
    errorMessage?: string;
    errorType?: string;
  };

  const trackError = ({
    errorCode,
    errorMessage,
    errorType,
  }: PropertyErrorProps) => {
    const fileNames: string[] = [];
    const fileTypes: string[] = [];
    const fileSizes: number[] = [];
    state?.files.forEach((file: File) => {
      fileNames.push(file.name);
      fileTypes.push(file.type);
      fileSizes.push(file.size);
    });
    trackEvent("PHE_Error", {
      id: "pool-update-submit-failed",
      numberOfFiles: state?.files?.length,
      errorCode,
      errorMessage,
      errorType,
      fileNames,
      fileTypes,
      fileSizes,
    });
  };

  if (submitting) {
    return (
      <div className={styles.loadingSpinner}>
        <SpinnerLoader firstMessage="Hang tight, we're sending your updates..." />
      </div>
    );
  }

  let totalSteps = NO_POOL_STEP_COUNT;
  if (state?.poolType === "In-ground") {
    totalSteps = IN_GROUND_POOL_STEP_COUNT;
  } else if (state?.poolType === "Above-ground") {
    totalSteps = ABOVE_GROUND_POOL_STEP_COUNT;
  }

  return (
    <MultiStepFormLayout
      step={totalSteps}
      totalSteps={totalSteps}
      heading="Does this look right?"
      buttonProps={{
        primary: {
          onPress: handleSubmit,
          text: "Yes, submit",
        },
        cancel: { path: "/my/property" },
        formNavigate,
      }}
    >
      <div className={styles.poolReview}>
        {error ? (
          <Alert
            appearance="danger"
            title="There was an error submitting your update."
            description={`Please try again. If the problem persists, contact support at ${supportPhoneNumber}.`}
          />
        ) : null}
        <Span>
          Swimming pool type: <Span bold>{state?.poolType}</Span>
        </Span>

        {state?.poolType !== "None" ? (
          <>
            {state?.poolType === "In-ground" ? (
              <>
                <Span>
                  Fenced: <Span bold>{state?.poolFence}</Span>
                </Span>
                {state?.poolFence === "Yes" ? (
                  <>
                    <Span>
                      Fence at least 4ft tall:{" "}
                      <Span bold>{state?.fenceFourFeet}</Span>
                    </Span>
                    <Span>
                      Fence with locking gate:{" "}
                      <Span bold>{state?.fenceLockingGate}</Span>
                    </Span>
                  </>
                ) : null}
              </>
            ) : null}
            <Span>
              Diving board: <Span bold>{state?.divingBoard}</Span>
            </Span>
            <Span>
              Slide: <Span bold>{state?.slide}</Span>
            </Span>
            <Span>
              Filled: <Span bold>{state?.filled}</Span>
            </Span>
            <Span>
              Attached to decking: <Span bold>{state?.poolDecking}</Span>
            </Span>
            {state?.poolDecking === "Yes" ? (
              <Span>
                Decking has locking gate:{" "}
                <Span bold>{state?.poolDeckGateLock}</Span>
              </Span>
            ) : null}
          </>
        ) : null}
        <div>
          <Span>Proof of update:</Span>
          <FileThumbnails files={state?.files} />
        </div>
      </div>
    </MultiStepFormLayout>
  );
};
